import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { PaperClipOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import Sheet from '@argo/common/components/Sheet'
import { api, claimable as canClaim, day, money, today, type Claim, type Policy } from '../api'
import { useSession } from '../session'

/**
 * The caller's claims — filed by them, or against their bond — and the form
 * that files one: on their own cover, or, as a landlord, on a bond naming them.
 */
export default function Claims() {
  const { t } = useTranslation()
  const { account } = useSession()
  const [filing, setFiling] = useState(false)

  const claims = useQuery({ queryKey: ['claims'], queryFn: () => api.claims(), refetchInterval: 15_000 })
  const policies = useQuery({ queryKey: ['policies'], queryFn: () => api.policies() })
  const claimable = canClaim(policies.data?.data ?? [], account)

  return (
    <div className="page page-mid stack">
      <div className="panel">
        <div className="panel-head">
          <span className="grow">{t('myClaims')}</span>
          <button className="btn-small" disabled={claimable.length === 0} onClick={() => setFiling(true)}>
            {t('makeClaim')}
          </button>
        </div>
        <div className="panel-body flush">
          {claims.isLoading && <div className="panel-body"><div className="skeleton" style={{ height: 60 }} /></div>}
          {!claims.isLoading && (claims.data?.data.length ?? 0) === 0 && <p className="empty">{t('noClaims')}</p>}
          {(claims.data?.data.length ?? 0) > 0 && <ClaimTable claims={claims.data!.data} />}
        </div>
      </div>

      {claimable.length === 0 && !policies.isLoading && (
        <p className="notice">{t('noPolicyToClaim')}</p>
      )}

      <FileSheet open={filing} onClose={() => setFiling(false)} policies={claimable} />
    </div>
  )
}

/** Shared with the insurer's queue, which shows the policyholder as well. */
export function ClaimTable({ claims, showHolder = false }: { claims: Claim[]; showHolder?: boolean }) {
  const { t } = useTranslation()
  return (
    <div className="scroll-x">
      <table className="table">
        <thead>
          <tr>
            <th>{t('coverWord')}</th>
            {showHolder && <th>{t('holder')}</th>}
            <th className="num">{t('claimed')}</th>
            <th className="num">{t('paidWord')}</th>
            <th className="num">{t('outstanding')}</th>
            <th>{t('incurredOn')}</th>
            <th>{t('reportedOn')}</th>
            <th>{t('status')}</th>
          </tr>
        </thead>
        <tbody>
          {claims.map(c => (
            <tr key={c.id}>
              <td>
                <Link to={`/claims/${c.id}`}>{c.product_name || c.policy}</Link>
              </td>
              {showHolder && <td>{c.holder}</td>}
              <td className="num">{money(c.amount)}</td>
              <td className="num">{money(c.paid)}</td>
              <td className="num">{money(c.outstanding)}</td>
              <td className="nowrap">{day(c.incurred_at)}</td>
              <td className="nowrap">{day(c.reported_at)}</td>
              <td><span className={`state ${c.status}`}>{t(c.status)}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function FileSheet({ open, onClose, policies }: { open: boolean; onClose: () => void; policies: Policy[] }) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [id, setId] = useState('')
  const [amount, setAmount] = useState('')
  const [when, setWhen] = useState(today())
  const [file, setFile] = useState<File | null>(null)
  const input = useRef<HTMLInputElement>(null)

  const policy = policies.find(p => p.id === id) ?? policies[0]
  const claimed = Number(amount)
  const valid = policy && file && Number.isFinite(claimed) && claimed > 0 && claimed <= Number(policy.remaining) && !!when

  const submit = useMutation({
    mutationFn: () => {
      const form = new FormData()
      form.set('policy', policy!.id)
      form.set('amount', String(claimed))
      form.set('incurred_on', when)
      form.set('evidence', file!)
      return api.file(form)
    },
    onSuccess: () => {
      message.success(t('claimFiled'))
      setAmount(''); setFile(null)
      qc.invalidateQueries({ queryKey: ['claims'] })
      qc.invalidateQueries({ queryKey: ['policies'] })
      qc.invalidateQueries({ queryKey: ['me'] })
      onClose()
    },
    onError: (e: Error) => message.error(e.message),
  })

  return (
    <Sheet open={open} title={t('makeClaim')} onClose={onClose}>
      <div className="stack" style={{ gap: 12 }}>
        <div className="field">
          <label htmlFor="policy">{t('cover')}</label>
          <div className="input-wrap">
            <select id="policy" value={policy?.id ?? ''} onChange={e => setId(e.target.value)}
              style={{ width: '100%', padding: '11px 12px', background: 'var(--surface-2)', color: 'var(--text)',
                       border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', fontWeight: 600 }}>
              {policies.map(p => (
                <option key={p.id} value={p.id}>
                  {(p.product_name || p.product)} · {t('coverLeft')} {money(p.remaining)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="field">
          <label htmlFor="amount">
            {t('claimAmount')}
            {policy && <b>{t('coverLeft')} {money(policy.remaining)}</b>}
          </label>
          <div className="input-wrap">
            <input id="amount" value={amount} onChange={e => setAmount(e.target.value.replace(/[^\d]/g, ''))}
              inputMode="numeric" placeholder="0" autoComplete="off" />
          </div>
        </div>

        <div className="field">
          <label htmlFor="when">{t('dateOfLoss')}</label>
          <div className="input-wrap">
            <input id="when" type="date" value={when} max={today()}
              min={policy ? policy.starts_at.slice(0, 10) : undefined}
              onChange={e => setWhen(e.target.value)} />
          </div>
        </div>

        <div className="file-field">
          <label>{t('evidence')}</label>
          <label className="file-drop">
            <input ref={input} type="file" accept="application/pdf,image/jpeg,image/png"
              onChange={e => setFile(e.target.files?.[0] ?? null)} />
            <PaperClipOutlined />
            <span className={file ? 'name' : 'muted'}>{file ? file.name : t('chooseFile')}</span>
          </label>
          <p className="field-hint">{t('fileTypes')}</p>
        </div>

        <p className="notice">{t('claimNotice')}</p>
        <p className="notice">{t('claimNoticeEvidence')}</p>

        <button className="submit buy" disabled={!valid || submit.isPending} onClick={() => submit.mutate()}>
          {t('submit')}
        </button>
      </div>
    </Sheet>
  )
}
